import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAgents } from '../../contexts/AgentContext';
import toast from 'react-hot-toast';
import SafeIcon from '../../common/SafeIcon';
import { FiExternalLink, FiSave, FiX } from 'react-icons/fi';


const AgentModal = ({ agent, onClose }) => {
  const { createAgent, updateAgent } = useAgents();
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    scenario: 'initial_outreach',
    voice: 'professional',
    targetAudience: [],
    thoughtlyAgentId: '',
    status: 'inactive'
  });

  const scenarios = [
    {
      key: 'initial_outreach',
      label: 'Initial Outreach',
      description: 'First contact with new life insurance agents',
      scriptPreview: 'Hi {firstName}, this is calling from Prosperity Leaders. I understand you\'re currently working in life insurance with {company}. We have an exciting opportunity that could significantly increase your earning potential...'
    },
    {
      key: 'follow_up',
      label: 'Follow-up Pro',
      description: 'Re-engagement for interested prospects',
      scriptPreview: 'Hi {firstName}, I\'m following up on our previous conversation about the opportunity with Prosperity Leaders. I wanted to see if you had a chance to review the information we discussed...'
    },
    {
      key: 'meeting_reminder',
      label: 'Meeting Coordinator',
      description: 'Confirmation calls for scheduled overview meetings',
      scriptPreview: 'Hi {firstName}, this is a friendly reminder about your overview meeting with Prosperity Leaders scheduled for tomorrow at {meetingTime}. We\'re excited to share more details about this opportunity...'
    },
    {
      key: 'reactivation',
      label: 'Re-activation Specialist',
      description: 'Contact cold leads after time periods',
      scriptPreview: 'Hi {firstName}, it\'s been a while since we last spoke about the opportunity with Prosperity Leaders. The market has evolved significantly, and we now have even more attractive options available...'
    }
  ];

  const voices = [
    { key: 'professional', label: 'Professional', description: 'Clear, authoritative tone' },
    { key: 'friendly', label: 'Friendly', description: 'Warm, approachable tone' },
    { key: 'authoritative', label: 'Authoritative', description: 'Confident, expert tone' },
    { key: 'natural', label: 'Natural', description: 'Conversational, relaxed tone' }
  ];

  const audienceOptions = [
    'Life Insurance Agents',
    'Experienced Agents',
    'New Agents',
    'Top Performers',
    'Career Changers',
    'Independent Agents'
  ];

  useEffect(() => {
    if (agent) {
      setFormData({
        name: agent.name || '',
        description: agent.description || '',
        scenario: agent.scenario || 'initial_outreach',
        voice: agent.voice || 'professional',
        targetAudience: agent.targetAudience || [],
        thoughtlyAgentId: agent.thoughtlyAgentId || '',
        status: agent.status || 'inactive'
      });
    }
  }, [agent]);

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.description) {
      toast.error('Agent name and description are required');
      return;
    }

    if (formData.targetAudience.length === 0) {
      toast.error('Please select at least one target audience');
      return;
    }

    const agentData = {
      ...formData,
      createdAt: agent ? agent.createdAt : new Date().toISOString(),
      stats: agent ? agent.stats : {
        totalCalls: 0,
        successfulCalls: 0,
        successRate: 0,
        avgDuration: '0:00'
      }
    };

    if (agent) {
      updateAgent(agent.id, agentData);
      toast.success('Agent updated successfully');
    } else {
      createAgent(agentData);
      toast.success('Agent created successfully');
    }

    onClose();
  };

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleAudienceToggle = (audience) => {
    setFormData(prev => ({
      ...prev,
      targetAudience: prev.targetAudience.includes(audience)
        ? prev.targetAudience.filter(a => a !== audience)
        : [...prev.targetAudience, audience]
    }));
  };

  const selectedScenario = scenarios.find(s => s.key === formData.scenario);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
      >
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            {agent ? 'Edit AI Agent' : 'Create New AI Agent'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <SafeIcon icon={FiX} className="text-gray-500" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Basic Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Agent Name *
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="e.g., Sarah - Initial Outreach"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Thoughtly Agent ID
              </label>
              <div className="flex">
                <input
                  type="text"
                  name="thoughtlyAgentId"
                  value={formData.thoughtlyAgentId}
                  onChange={handleChange}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-l-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="agent_abc123"
                />
                <a
                  href="https://app.thoughtly.co"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-3 py-2 bg-gray-100 border border-l-0 border-gray-300 rounded-r-lg hover:bg-gray-200 transition-colors"
                  title="Open Thoughtly Dashboard"
                >
                  <SafeIcon icon={FiExternalLink} className="text-gray-600" />
                </a>
              </div>
              <p className="mt-1 text-xs text-gray-500">
                Find this ID in your Thoughtly dashboard
              </p>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description *
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              required
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Brief description of the agent's purpose and role"
            />
          </div>

          {/* Recruitment Scenario */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Recruitment Scenario *
            </label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {scenarios.map(scenario => (
                <div
                  key={scenario.key}
                  className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                    formData.scenario === scenario.key
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  onClick={() => setFormData(prev => ({ ...prev, scenario: scenario.key }))}
                >
                  <div className="flex items-center mb-2">
                    <input
                      type="radio"
                      name="scenario"
                      value={scenario.key}
                      checked={formData.scenario === scenario.key}
                      onChange={handleChange}
                      className="mr-3 text-blue-600 focus:ring-blue-500"
                    />
                    <h4 className="font-medium text-gray-900">{scenario.label}</h4>
                  </div>
                  <p className="text-sm text-gray-600">{scenario.description}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Script Preview */}
          {selectedScenario && (
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-medium text-gray-900">Script Preview</h4>
                <a
                  href="https://app.thoughtly.co"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 text-blue-600 hover:text-blue-700 text-sm"
                >
                  Edit in Thoughtly <SafeIcon icon={FiExternalLink} className="text-xs" />
                </a>
              </div>
              <p className="text-sm text-gray-700 leading-relaxed">
                {selectedScenario.scriptPreview}
              </p>
              <p className="text-xs text-gray-500 mt-2">
                This is a preview. Full script editing is done in the Thoughtly dashboard.
              </p>
            </div>
          )}

          {/* Voice Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Voice Style
            </label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {voices.map(voice => (
                <div
                  key={voice.key}
                  className={`p-3 border rounded-lg cursor-pointer transition-colors text-center ${
                    formData.voice === voice.key
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  onClick={() => setFormData(prev => ({ ...prev, voice: voice.key }))}
                >
                  <input
                    type="radio"
                    name="voice"
                    value={voice.key}
                    checked={formData.voice === voice.key}
                    onChange={handleChange}
                    className="sr-only"
                  />
                  <h5 className="font-medium text-gray-900 mb-1">{voice.label}</h5>
                  <p className="text-xs text-gray-600">{voice.description}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Target Audience */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Target Audience *
            </label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {audienceOptions.map(audience => (
                <div
                  key={audience}
                  className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                    formData.targetAudience.includes(audience)
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  onClick={() => handleAudienceToggle(audience)}
                >
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.targetAudience.includes(audience)}
                      onChange={() => handleAudienceToggle(audience)}
                      className="mr-2 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm font-medium text-gray-900">{audience}</span>
                  </div>
                </div>
              ))}
            </div>
            <p className="mt-2 text-sm text-gray-500">
              Selected: {formData.targetAudience.join(', ') || 'None'}
            </p>
          </div>

          {/* Thoughtly Integration Info */}
          <div className="bg-blue-50 rounded-lg p-4">
            <h4 className="font-medium text-blue-900 mb-2">Thoughtly Integration</h4>
            <p className="text-sm text-blue-700 mb-3">
              This agent will use your configured Thoughtly API to make calls. Make sure you have:
            </p>
            <ul className="text-sm text-blue-700 space-y-1 list-disc list-inside">
              <li>Valid Thoughtly API key configured in settings</li>
              <li>Sufficient call credits in your Thoughtly account</li>
              <li>Agent script configured in Thoughtly dashboard</li>
            </ul>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <SafeIcon icon={FiSave} className="text-sm" />
              {agent ? 'Update' : 'Create'} Agent
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

export default AgentModal;